import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import {
    ReportTimeframeSelector,
    WeekSelector,
    getCurrentWeek,
    dateToLocalDateString,
} from './weeklyReports';
import styles from './reports.module.scss';

// -----------------------------------------------------------------------------
//  Constants
// -----------------------------------------------------------------------------
const MIN_PERIODS = 1;
const MAX_PERIODS = 5;
const DEFAULT_PERIODS = 3;

// Labels that drive specific per-mile cost metrics. Constants so the backend
// can rename categories independently of the rest of the UI.
const FUEL_LABEL = 'Fuel';
const MAINTENANCE_LABEL = 'Maintenance';

// Fixed expense-row order (salary is rendered separately). Remaining dynamic
// labels are sorted by sum across the currently visible periods.
const PINNED_EXPENSE_LABELS = [
    'Fuel - Lease/Own',
    'Fuel - Company',
    'Fuel (EFS - Lease/Own)',
    'Fuel (EFS - Company)',
    'Fuel',
    'Fuel (EFS)',
];

/** Ownership-split fuel categories (card + EFS). Unclassified Fuel is excluded. */
const FUEL_LABELS_COMPANY = ['Fuel - Company', 'Fuel (EFS - Company)'];
const FUEL_LABELS_LEASE_OWN = ['Fuel - Lease/Own', 'Fuel (EFS - Lease/Own)'];

const OTHER_EFS_LABEL = 'Miscellaneous Expenses';
const MAINTENANCE_EFS_LABEL = 'Maintenance (EFS)';

/** Non-fuel, non-maintenance `* (EFS)` rows — collapsed under Miscellaneous Expenses. */
function isOtherEfsExpenseLabel(label) {
    if (!label || !label.endsWith(' (EFS)')) return false;
    if (label === MAINTENANCE_EFS_LABEL) return false;
    if (label.startsWith('Fuel')) return false;
    return true;
}

// Thresholds for the gross-change percentage badge. First tier the value is
// BELOW wins.
const GROSS_CHANGE_TIERS = [
    { max: -0.20, className: 'changeDarkRed' },
    { max: -0.10, className: 'changeRed' },
    { max:  0.00, className: 'changeLightRed' },
    { max:  0.10, className: 'changeLightGreen' },
    { max:  0.20, className: 'changeGreen' },
    { max: Infinity, className: 'changeDarkGreen' },
];

// -----------------------------------------------------------------------------
//  Date / period helpers
// -----------------------------------------------------------------------------
function shiftPeriodStart(start, timeframe, direction) {
    const d = new Date(start);
    switch (timeframe) {
        case 'weekly':    d.setDate(d.getDate() + direction * 7); break;
        case 'biweekly':  d.setDate(d.getDate() + direction * 14); break;
        case 'monthly':   d.setMonth(d.getMonth() + direction); break;
        case 'quarterly': d.setMonth(d.getMonth() + direction * 3); break;
        case 'yearly':    d.setFullYear(d.getFullYear() + direction); break;
        default: break;
    }
    return d;
}

function endOfPeriod(start, timeframe) {
    const s = new Date(start);
    const e = new Date(s);
    switch (timeframe) {
        case 'weekly':    e.setDate(s.getDate() + 6); break;
        case 'biweekly':  e.setDate(s.getDate() + 13); break;
        case 'monthly':   e.setMonth(s.getMonth() + 1); e.setDate(0); break;
        case 'quarterly': e.setMonth(s.getMonth() + 3); e.setDate(0); break;
        case 'yearly':    e.setFullYear(s.getFullYear() + 1); e.setMonth(0); e.setDate(0); break;
        default: break;
    }
    return e;
}

function currentCompletedPeriodStart(timeframe, firstDayOfAWeek) {
    const now = new Date();
    switch (timeframe) {
        case 'weekly':    return getCurrentWeek(firstDayOfAWeek, true);
        case 'biweekly': {
            const s = getCurrentWeek(firstDayOfAWeek, true);
            s.setDate(s.getDate() - 7);
            return s;
        }
        case 'monthly':   return new Date(now.getFullYear(), now.getMonth() - 1, 1);
        case 'quarterly': {
            const qStart = Math.floor(now.getMonth() / 3) * 3 - 3;
            return new Date(now.getFullYear(), qStart, 1);
        }
        case 'yearly':    return new Date(now.getFullYear() - 1, 0, 1);
        default:          return getCurrentWeek(firstDayOfAWeek, true);
    }
}

function cacheKey(start, timeframe) {
    return `${timeframe}|${dateToLocalDateString(start)}`;
}

/** e.g. Mar 28 – Apr 3; adds year when the span crosses calendar years or when forced (multi-week bar). */
function formatWeekDateRange(start, end, { forceYear = false, separator = ' – ' } = {}) {
    const crossesCalendarYears = start.getFullYear() !== end.getFullYear();
    const needYear = forceYear || crossesCalendarYears;
    const opts = needYear
        ? { month: 'short', day: 'numeric', year: 'numeric' }
        : { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)}${separator}${end.toLocaleDateString('en-US', opts)}`;
}

/** Q3 '25 — straight apostrophe to match app copy */
function quarterShortLabel(d) {
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} '${String(d.getFullYear()).slice(2)}`;
}

// Label used in period column headers.
function periodColumnLabel(start, end, timeframe, monthlyRangeCrossesYears) {
    switch (timeframe) {
        case 'monthly':
            return start.toLocaleDateString(
                'en-US',
                monthlyRangeCrossesYears
                    ? { month: 'long', year: 'numeric' }
                    : { month: 'long' }
            );
        case 'quarterly':
            return quarterShortLabel(start);
        case 'yearly':
            return `${start.getFullYear()}`;
        case 'weekly':
        case 'biweekly':
        default:
            return formatWeekDateRange(start, end, { separator: ' – ' });
    }
}

/** ISO `YYYY-MM-DD` as a local calendar date. `new Date(iso)` is UTC and shifts the day. */
function parseIsoLocalDate(value) {
    if (typeof value !== 'string') return null;
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

/** Assigned-period span for monthly/quarterly headers, e.g. "May 29 - Jun 30". */
function coverageRangeLabel(data) {
    const start = parseIsoLocalDate(data && data.coverageStart);
    const end = parseIsoLocalDate(data && data.coverageEnd);
    if (!start || !end) return null;
    return formatWeekDateRange(start, end, { separator: ' - ' });
}

/** Range shown in WeekSelector (spans all visible period columns). */
function windowRangeLabelForTimeframe(firstPeriod, lastPeriod, timeframe) {
    const first = firstPeriod.start;
    const lastStart = lastPeriod.start;
    const lastEnd = lastPeriod.end;

    switch (timeframe) {
        case 'weekly':
        case 'biweekly': {
            const crossesYears = first.getFullYear() !== lastEnd.getFullYear();
            return formatWeekDateRange(first, lastEnd, { forceYear: crossesYears, separator: ' - ' });
        }
        case 'monthly': {
            const sameYear = first.getFullYear() === lastEnd.getFullYear();
            const a = first.toLocaleDateString('en-US', { month: 'short' });
            const b = lastEnd.toLocaleDateString('en-US', { month: 'short' });
            if (sameYear) return `${a} - ${b}`;
            return `${a} '${String(first.getFullYear()).slice(2)} - ${b} '${String(lastEnd.getFullYear()).slice(2)}`;
        }
        case 'quarterly':
            return `${quarterShortLabel(first)} - ${quarterShortLabel(lastStart)}`;
        case 'yearly':
            return `${first.getFullYear()} - ${lastStart.getFullYear()}`;
        default:
            return formatWeekDateRange(first, lastEnd, { separator: ' - ' });
    }
}

// -----------------------------------------------------------------------------
//  API: GET reports/fleet-performance
// -----------------------------------------------------------------------------
function normalizeFleetPeriodsResponse(res) {
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.periods)) return res.periods;
    if (res && Array.isArray(res.dataset)) return res.dataset;
    return [];
}

/** API may return `{ noData: true }` for a period with no metrics. */
function isFleetPeriodNoData(period) {
    return Boolean(period && typeof period === 'object' && period.noData === true);
}

function fleetPerformanceUrl(startDate, endDate, timeframe, loadPrevForComparasion) {
    const qs = new URLSearchParams({
        startDate,
        endDate,
        timeframe,
        loadPrevForComparasion: loadPrevForComparasion ? 'true' : 'false',
    }).toString();
    return `${API_ROOT}reports/fleet-performance?${qs}`;
}

// -----------------------------------------------------------------------------
//  Derived-value helpers
// -----------------------------------------------------------------------------
function findExpenseAmount(period, label, allLookALikes) {
    if (!period || !period.expenses) return 0;
    if (allLookALikes) {
        const needle = label.toLowerCase();
        return period.expenses.reduce(
            (sum, e) =>
                e.label && e.label.toLowerCase().includes(needle)
                    ? sum + (Number(e.amount) || 0)
                    : sum,
            0,
        );
    }
    const match = period.expenses.find((e) => e.label === label);
    return match ? (Number(match.amount) || 0) : 0;
}
function sumExpenses(period) {
    if (!period || !period.expenses) return 0;
    return period.expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
}

function sumExpenseLabels(period, labels) {
    return labels.reduce((s, label) => s + findExpenseAmount(period, label), 0);
}

/** Gross − Salary − Fuel, split by Company vs Lease/Own (matches ownership fuel labels). */
function computePayFuelMargin(period) {
    if (!period) return null;
    const fuelCompany = sumExpenseLabels(period, FUEL_LABELS_COMPANY);
    const fuelLeaseOwn = sumExpenseLabels(period, FUEL_LABELS_LEASE_OWN);
    const profitCompany = period.grossCompany - period.salaryCompany - fuelCompany;
    const profitLeaseOwn = period.grossLeaseOwn - period.salaryLeaseOwn - fuelLeaseOwn;
    const pctOf = (part, gross) => (gross ? part / gross : null);
    return {
        fuelCompany,
        fuelLeaseOwn,
        profitCompany,
        profitLeaseOwn,
        salaryPctCompany: pctOf(period.salaryCompany, period.grossCompany),
        salaryPctLeaseOwn: pctOf(period.salaryLeaseOwn, period.grossLeaseOwn),
        fuelPctCompany: pctOf(fuelCompany, period.grossCompany),
        fuelPctLeaseOwn: pctOf(fuelLeaseOwn, period.grossLeaseOwn),
        profitPctCompany: pctOf(profitCompany, period.grossCompany),
        profitPctLeaseOwn: pctOf(profitLeaseOwn, period.grossLeaseOwn),
    };
}

function computeDerived(period) {
    if (!period) return null;
    const totalGross   = period.grossCompany + period.grossLeaseOwn;
    const totalSalary  = period.salaryCompany + period.salaryLeaseOwn;
    const totalExp     = totalSalary + sumExpenses(period);
    const opProfit     = totalGross - totalExp;
    const totalMiles   = period.milesCompany + period.milesLeaseOwn;
    const totalTrucksUtilized = period.companyTruckCount + period.leaseOwnTruckCount;
    return {
        totalGross, totalExp, opProfit, totalMiles, totalTrucksUtilized, totalSalary,
        opProfitPct: totalGross ? opProfit / totalGross : 0,
        utilization: period.totalAvailableTrucks ? totalTrucksUtilized / period.totalAvailableTrucks : 0,
        avgMileCompany:        period.companyTruckCount  ? period.milesCompany  / period.companyTruckCount  : 0,
        avgMileLeaseOwn:       period.leaseOwnTruckCount ? period.milesLeaseOwn / period.leaseOwnTruckCount : 0,
        driverSalaryPctCompany:  period.grossCompany  ? period.salaryCompany  / period.grossCompany  : 0,
        driverSalaryPctLeaseOwn: period.grossLeaseOwn ? period.salaryLeaseOwn / period.grossLeaseOwn : 0,
        driverCostPerMiCompany:  period.milesCompany  ? period.salaryCompany  / period.milesCompany  : 0,
        fuelCostPerMi:         totalMiles ? findExpenseAmount(period, FUEL_LABEL, true)        / totalMiles : 0,
        maintenanceCostPerMi:  totalMiles ? findExpenseAmount(period, MAINTENANCE_LABEL, true) / totalMiles : 0,
        avgGrossCompany:       period.companyTruckCount  ? period.grossCompany  / period.companyTruckCount  : 0,
        avgGrossLeaseOwn:      period.leaseOwnTruckCount ? period.grossLeaseOwn / period.leaseOwnTruckCount : 0,
        totalAvgGross:         totalTrucksUtilized ? totalGross    / totalTrucksUtilized : 0,
        rpmCompany:            period.milesCompany  ? period.grossCompany  / period.milesCompany  : 0,
        rpmLeaseOwn:           period.milesLeaseOwn ? period.grossLeaseOwn / period.milesLeaseOwn : 0,
        totalRpm:              totalMiles ? totalGross / totalMiles : 0,
        ...computePayFuelMargin(period),
    };
}

// -----------------------------------------------------------------------------
//  Formatting helpers
// -----------------------------------------------------------------------------
const fmtMoney  = (v) => (v == null ? '—' : priceOnFinish(v, false, true));
const fmtRate   = (v) => (v == null ? '—' : `$${(Number(v) || 0).toFixed(2)}`);
const fmtPct    = (v, digits = 2) => (v == null ? '—' : `${((Number(v) || 0) * 100).toFixed(digits)}%`);
const fmtMiles  = (v) => (v == null ? '—' : Math.round(Number(v) || 0).toLocaleString('en-US'));
const fmtCount1 = (v) => (v == null ? '—' : (Number(v) || 0).toFixed(1));

/** Compact Company | Lease/Own amount (+ optional % of segment gross) for one period cell. */
const PayFuelSegmentCell = ({
    companyAmt,
    leaseAmt,
    companyPct,
    leasePct,
    emphasize = false,
    showHeads = false,
}) => (
    <div className={`${styles.payFuelCell} ${emphasize ? styles.payFuelCellProfit : ''}`}>
        <div className={styles.payFuelSeg}>
            {showHeads ? (
                <span className={styles.payFuelHead}>
                    <i className={`${styles.dot} ${styles.barCompany}`} /> Company
                </span>
            ) : null}
            <span className={styles.payFuelAmt}>{fmtMoney(companyAmt)}</span>
            {companyPct != null ? <span className={styles.payFuelPct}>{fmtPct(companyPct, 1)}</span> : null}
        </div>
        <div className={styles.payFuelSeg}>
            {showHeads ? (
                <span className={styles.payFuelHead}>
                    <i className={`${styles.dot} ${styles.barLeaseOwn}`} /> Lease/Own
                </span>
            ) : null}
            <span className={styles.payFuelAmt}>{fmtMoney(leaseAmt)}</span>
            {leasePct != null ? <span className={styles.payFuelPct}>{fmtPct(leasePct, 1)}</span> : null}
        </div>
    </div>
);

function classifyGrossChange(pct) {
    for (const tier of GROSS_CHANGE_TIERS) {
        if (pct < tier.max) return tier.className;
    }
    return GROSS_CHANGE_TIERS[GROSS_CHANGE_TIERS.length - 1].className;
}

// -----------------------------------------------------------------------------
//  Reusable small components
// -----------------------------------------------------------------------------
const ChangeBadge = ({ pct }) => {
    if (pct == null || !isFinite(pct)) {
        return <span className={`${styles.changeBadge} ${styles.changeBadgeEmpty}`} />;
    }
    const cls = classifyGrossChange(pct);
    const sign = pct > 0 ? '+' : '';
    return (
        <span className={`${styles.changeBadge} ${styles[cls]}`}>
            {sign}{(pct * 100).toFixed(2)}%
        </span>
    );
};

// Compact utilization donut used in-cell. Keeps the % readable at small sizes.
const UtilizationCell = ({ value }) => {
    const pct = Math.max(0, Math.min(1, value || 0));
    const angle = pct * 360;
    const style = {
        background: `conic-gradient(var(--main-color) ${angle}deg, var(--extra-light-gray) ${angle}deg 360deg)`,
    };
    return (
        <div className={styles.utilCell}>
            <div className={styles.utilRing} style={style}>
                <div className={styles.utilRingInner}>
                    <span>{Math.round(pct * 100)}%</span>
                </div>
            </div>
        </div>
    );
};

// Generic 2-segment proportion bar (used beneath Total Gross / Miles).
const ProportionBar = ({ primaryPct, primaryClass = 'barCompany', secondaryClass = 'barLeaseOwn', title, height }) => {
    const p = Math.max(0, Math.min(1, primaryPct || 0));
    return (
        <div className={styles.propBar} style={height ? { height } : undefined} title={title}>
            <div className={`${styles.propBarPart} ${styles[primaryClass]}`}   style={{ flexBasis: `${p * 100}%` }} />
            <div className={`${styles.propBarPart} ${styles[secondaryClass]}`} style={{ flexBasis: `${(1 - p) * 100}%` }} />
        </div>
    );
};

// -----------------------------------------------------------------------------
//  Main component
// -----------------------------------------------------------------------------
export const FleetPerformanceReport = ({ companyData }) => {
    const firstDayOfAWeek = companyData?.firstDayOfAWeek || 'monday';

    const [timeframe, setTimeframe] = useState('weekly');
    const [periodCount, setPeriodCount] = useState(DEFAULT_PERIODS);
    const [anchorStart, setAnchorStart] = useState(() => currentCompletedPeriodStart('weekly', firstDayOfAWeek));
    const [dataCache, setDataCache] = useState({});
    const [otherEfsExpanded, setOtherEfsExpanded] = useState(false);
    /** null = treat next fetch as bulk (initial / timeframe / period count change). */
    const prevAnchorMsRef = useRef(null);

    const visiblePeriods = useMemo(() => {
        const arr = [];
        for (let i = periodCount - 1; i >= 0; i--) {
            const start = shiftPeriodStart(anchorStart, timeframe, -i);
            arr.push({ start, end: endOfPeriod(start, timeframe) });
        }
        return arr;
    }, [anchorStart, timeframe, periodCount]);

    const comparisonPeriod = useMemo(() => {
        const first = visiblePeriods[0];
        const start = shiftPeriodStart(first.start, timeframe, -1);
        return { start, end: endOfPeriod(start, timeframe) };
    }, [visiblePeriods, timeframe]);

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();
        const firstVis = visiblePeriods[0];
        const lastVis = visiblePeriods[visiblePeriods.length - 1];
        if (!firstVis || !lastVis) return undefined;

        const anchorMs = anchorStart.getTime();
        const prevMs = prevAnchorMsRef.current;
        let loadType = 'bulk';
        if (prevMs !== null) {
            if (anchorMs < prevMs) loadType = 'left';
            else if (anchorMs > prevMs) loadType = 'right';
            else return undefined;
        }

        let startDate;
        let endDate;
        let loadPrevForComparasion;
        const keysToPlaceholder = [];

        if (loadType === 'bulk') {
            startDate = dateToLocalDateString(comparisonPeriod.start);
            endDate = dateToLocalDateString(lastVis.end);
            loadPrevForComparasion = true;
            keysToPlaceholder.push(cacheKey(comparisonPeriod.start, timeframe));
            visiblePeriods.forEach((p) => keysToPlaceholder.push(cacheKey(p.start, timeframe)));
        } else if (loadType === 'left') {
            startDate = dateToLocalDateString(comparisonPeriod.start);
            endDate = dateToLocalDateString(firstVis.end);
            loadPrevForComparasion = true;
            keysToPlaceholder.push(cacheKey(comparisonPeriod.start, timeframe));
            keysToPlaceholder.push(cacheKey(firstVis.start, timeframe));
        } else {
            startDate = dateToLocalDateString(lastVis.start);
            endDate = dateToLocalDateString(lastVis.end);
            loadPrevForComparasion = false;
            keysToPlaceholder.push(cacheKey(lastVis.start, timeframe));
        }

        setDataCache((prev) => {
            const next = { ...prev };
            keysToPlaceholder.forEach((k) => {
                if (next[k] === undefined) next[k] = null;
            });
            return next;
        });

        const link = fleetPerformanceUrl(startDate, endDate, timeframe, loadPrevForComparasion);
        sendRequest(
            'GET',
            link,
            ac.signal,
            (res) => {
                if (cancelled) return;
                const periods = normalizeFleetPeriodsResponse(res);
                setDataCache((prev) => {
                    const next = { ...prev };
                    if (loadType === 'bulk') {
                        const need = periodCount + 1;
                        if (periods.length < need) {
                            addNotification({
                                message: `Fleet performance: expected ${need} periods, got ${periods.length}.`,
                                type: 'alert',
                            });
                        }
                        if (periods.length >= 1) {
                            next[cacheKey(comparisonPeriod.start, timeframe)] = periods[0];
                        }
                        for (let i = 0; i < periodCount && i + 1 < periods.length; i++) {
                            next[cacheKey(visiblePeriods[i].start, timeframe)] = periods[i + 1];
                        }
                    } else if (loadType === 'left') {
                        if (periods.length < 2) {
                            addNotification({
                                message: 'Fleet performance: expected 2 periods when loading older window.',
                                type: 'alert',
                            });
                        }
                        if (periods.length >= 1) {
                            next[cacheKey(comparisonPeriod.start, timeframe)] = periods[0];
                        }
                        if (periods.length >= 2) {
                            next[cacheKey(firstVis.start, timeframe)] = periods[1];
                        }
                    } else if (periods.length >= 1) {
                        next[cacheKey(lastVis.start, timeframe)] = periods[0];
                    } else {
                        addNotification({
                            message: 'Fleet performance: empty response for new period.',
                            type: 'alert',
                        });
                    }
                    return next;
                });
                prevAnchorMsRef.current = anchorMs;
            },
            () => {
                if (!cancelled) {
                    addNotification({ message: 'Could not load fleet performance report.', type: 'alert' });
                    setDataCache((prev) => {
                        const next = { ...prev };
                        keysToPlaceholder.forEach((k) => {
                            delete next[k];
                        });
                        return next;
                    });
                }
            },
            null,
        );

        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [anchorStart, timeframe, periodCount, visiblePeriods, comparisonPeriod]);

    const getPeriodData = useCallback(
        (p) => dataCache[cacheKey(p.start, timeframe)] || null,
        [dataCache, timeframe]
    );

    const handleTimeframeChange = (tf) => {
        if (tf === timeframe) return;
        setTimeframe(tf);
        setAnchorStart(currentCompletedPeriodStart(tf, firstDayOfAWeek));
        setDataCache({});
        prevAnchorMsRef.current = null;
    };
    const shiftWindow = (direction) =>
        setAnchorStart((prev) => shiftPeriodStart(prev, timeframe, direction));

    // Pinned fuel rows first; remaining labels (and Miscellaneous Expenses aggregate) sorted
    // by sum across visible periods. Fuel / Maintenance (EFS) stay uncollapsed.
    const dynamicExpenseRows = useMemo(() => {
        const totals = new Map();
        visiblePeriods.forEach(p => {
            const d = getPeriodData(p);
            if (!d || isFleetPeriodNoData(d) || !d.expenses) return;
            d.expenses.forEach(e => {
                if (!e.label) return;
                totals.set(e.label, (totals.get(e.label) || 0) + (Number(e.amount) || 0));
            });
        });

        const pinnedSet = new Set(PINNED_EXPENSE_LABELS);
        const pinned = PINNED_EXPENSE_LABELS
            .filter(label => totals.has(label))
            .map(label => ({ type: 'label', label }));

        const otherEfsEntries = [];
        const restEntries = [];
        let otherEfsTotal = 0;
        totals.forEach((total, label) => {
            if (pinnedSet.has(label)) return;
            if (isOtherEfsExpenseLabel(label)) {
                otherEfsEntries.push([label, total]);
                otherEfsTotal += total;
            } else {
                restEntries.push([label, total]);
            }
        });

        otherEfsEntries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
        const merge = restEntries.map(([label, total]) => ({ type: 'label', label, total }));
        if (otherEfsEntries.length > 0) {
            merge.push({
                type: 'otherEfs',
                label: OTHER_EFS_LABEL,
                total: otherEfsTotal,
                detailLabels: otherEfsEntries.map(([label]) => label),
            });
        }
        merge.sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));

        return [
            ...pinned,
            ...merge.map(({ type, label, detailLabels }) => (
                type === 'otherEfs' ? { type, label, detailLabels } : { type, label }
            )),
        ];
    }, [visiblePeriods, getPeriodData]);

    const anyDataLoaded = visiblePeriods.some(p => !!getPeriodData(p));

    const monthlyRangeCrossesYears = useMemo(() => {
        if (timeframe !== 'monthly' || !visiblePeriods.length) return false;
        return visiblePeriods[0].start.getFullYear()
            !== visiblePeriods[visiblePeriods.length - 1].end.getFullYear();
    }, [timeframe, visiblePeriods]);

    const windowRangeLabel = useMemo(() => {
        const first = visiblePeriods[0];
        const last = visiblePeriods[visiblePeriods.length - 1];
        if (!first || !last) return '';
        return windowRangeLabelForTimeframe(first, last, timeframe);
    }, [visiblePeriods, timeframe]);

    const prevPeriodFor = (idx) => idx === 0 ? comparisonPeriod : visiblePeriods[idx - 1];

    const grossChangeForIdx = (idx) => {
        const cur = getPeriodData(visiblePeriods[idx]);
        const prv = getPeriodData(prevPeriodFor(idx));
        if (!cur || !prv || isFleetPeriodNoData(cur) || isFleetPeriodNoData(prv)) return null;
        const c = cur.grossCompany + cur.grossLeaseOwn;
        const p = prv.grossCompany + prv.grossLeaseOwn;
        return p > 0 ? (c - p) / p : null;
    };

    const gridStyle = { '--fleet-col-count': periodCount };

    // -------------------------------------------------------------------------
    //  Row primitives — each wraps grid cells in .tblRow for whole-row hover.
    // -------------------------------------------------------------------------
    const SectionRow = ({ label, subLabel }) => (
        <div className={styles.tblSectionRow}>
            <span>{label}</span>
            {subLabel ? <small>{subLabel}</small> : null}
        </div>
    );

    const LoadingValue = () => <span className={styles.cellLoading}>…</span>;
    const NoDataValue = () => <span className={styles.cellNoData}>No Data</span>;

    // Simple metric row: label + N values.
    const MetricRow = ({ label, accessor, kind = 'plain' /* plain | muted | total */, valueClassAccessor }) => (
        <div className={styles.tblRow}>
            <div className={`${styles.tblLabel} ${kind === 'total' ? styles.tblLabelTotal : ''} ${kind === 'muted' ? styles.tblLabelMuted : ''}`}>
                {label}
            </div>
            {visiblePeriods.map((p, idx) => {
                const d = getPeriodData(p);
                const extraCls = d && !isFleetPeriodNoData(d) && valueClassAccessor ? valueClassAccessor(d) : '';
                return (
                    <div key={idx} className={`${styles.tblValue} ${kind === 'total' ? styles.tblValueTotal : ''} ${kind === 'muted' ? styles.tblValueMuted : ''} ${extraCls ? styles[extraCls] : ''}`}>
                        {!d ? <LoadingValue /> : isFleetPeriodNoData(d) ? <NoDataValue /> : accessor(d, p)}
                    </div>
                );
            })}
        </div>
    );

    // Row whose cells render a fully-custom element per period (e.g. utilization
    // ring, proportion bars, margin pills).
    const CustomRow = ({
        label,
        kind = 'plain',
        renderCell,
        indented = false,
        expandable = false,
        expanded = false,
        onLabelClick,
    }) => (
        <div className={styles.tblRow}>
            <div
                className={[
                    styles.tblLabel,
                    kind === 'total' ? styles.tblLabelTotal : '',
                    kind === 'muted' ? styles.tblLabelMuted : '',
                    indented ? styles.tblLabelIndented : '',
                    onLabelClick ? styles.tblLabelClickable : '',
                ].filter(Boolean).join(' ')}
                onClick={onLabelClick}
                onKeyDown={onLabelClick ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onLabelClick();
                    }
                } : undefined}
                role={onLabelClick ? 'button' : undefined}
                tabIndex={onLabelClick ? 0 : undefined}
                aria-expanded={expandable ? expanded : undefined}
            >
                {expandable ? (
                    <span className={styles.tblExpandLabel}>
                        <span className={styles.tblExpandChevron} aria-hidden>
                            {expanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                        {label}
                    </span>
                ) : label}
            </div>
            {visiblePeriods.map((p, idx) => {
                const d = getPeriodData(p);
                return (
                    <div key={idx} className={`${styles.tblValue} ${kind === 'total' ? styles.tblValueTotal : ''} ${styles.tblValueCustom}`}>
                        {!d ? <LoadingValue /> : isFleetPeriodNoData(d) ? <NoDataValue /> : renderCell(d, p, idx)}
                    </div>
                );
            })}
        </div>
    );

    // -------------------------------------------------------------------------
    //  Header band: weekly/biweekly/yearly show the calendar label. Monthly and
    //  quarterly append the assigned period span, e.g. June (May 29 - Jun 30).
    // -------------------------------------------------------------------------
    const HeaderRow = () => {
        return (
            <div className={`${styles.tblRow} ${styles.tblRowStatic}`}>
                <div className={styles.tblCorner}>
                    <div className={styles.tblCornerLabel}>Metric</div>
                    <div className={styles.tblCornerHint}>vs. prior {timeframe === 'weekly' ? 'week' : 'period'}</div>
                </div>
                {visiblePeriods.map((p, idx) => {
                    const range = (timeframe === 'monthly' || timeframe === 'quarterly')
                        ? coverageRangeLabel(getPeriodData(p))
                        : null;
                    return (
                        <div key={idx} className={styles.tblPeriodHead}>
                            <div className={styles.tblPeriodRange}>
                                {periodColumnLabel(p.start, p.end, timeframe, monthlyRangeCrossesYears)}
                                {range ? <span className={styles.tblPeriodCoverage}> ({range})</span> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // -------------------------------------------------------------------------
    //  Render
    // -------------------------------------------------------------------------
    return (
        <div className={styles.mainContainer}>
            <div className={styles.fleetTopControls}>
                <div className={styles.fleetTopSpacer} />
                <div className={styles.fleetTopCenter}>
                    <ReportTimeframeSelector
                        selectedTimeframe={timeframe}
                        onChange={handleTimeframeChange}
                        allowCustomTimeframe={false}
                    />
                </div>
                <div className={styles.fleetTopRight}>
                    <select
                        className={styles.periodCountSelect}
                        value={periodCount}
                        onChange={(e) => {
                            setPeriodCount(Number(e.target.value));
                            setDataCache({});
                            prevAnchorMsRef.current = null;
                        }}
                        aria-label="Number of periods"
                    >
                        {Array.from({ length: MAX_PERIODS - MIN_PERIODS + 1 }, (_, i) => i + MIN_PERIODS).map(n => (
                            <option key={n} value={n}>{n} {n === 1 ? 'period' : 'periods'}</option>
                        ))}
                    </select>
                </div>
            </div>

            <WeekSelector>
                <FaChevronLeft onClick={() => shiftWindow(-1)} />
                <span className={`non-selectable ${styles.fleetRangeLabel}`}>{windowRangeLabel}</span>
                <FaChevronRight onClick={() => shiftWindow(1)} />
            </WeekSelector>

            {!anyDataLoaded ? (
                <div className={styles.fleetLoadingState}>Loading…</div>
            ) : (
                <div className={styles.tblWrap}>
                    <div
                        className={`${styles.tbl} ${(timeframe === 'monthly' || timeframe === 'quarterly') ? styles.tblCalendarBuckets : ''}`}
                        style={gridStyle}
                    >
                        <HeaderRow />

                        {/* ---------------- REVENUE ---------------- */}
                        <SectionRow label="Revenue" subLabel="Total Gross = Company + Lease/Own" />
                        <MetricRow
                            label="Gross — Company"
                            kind="muted"
                            accessor={(d) => fmtMoney(d.grossCompany)}
                        />
                        <MetricRow
                            label="Gross — Lease/Own"
                            kind="muted"
                            accessor={(d) => fmtMoney(d.grossLeaseOwn)}
                        />
                        <CustomRow
                            label="Total Gross"
                            kind="total"
                            renderCell={(d, _p, idx) => {
                                const total = d.grossCompany + d.grossLeaseOwn;
                                const coShare = total > 0 ? d.grossCompany / total : 0;
                                const pct = grossChangeForIdx(idx);
                                return (
                                    <div className={styles.totalGrossCell}>
                                        <div className={styles.totalGrossTopRow}>
                                            <span className={styles.totalGrossValue}>{fmtMoney(total)}</span>
                                            <ChangeBadge pct={pct} />
                                        </div>
                                        <ProportionBar
                                            primaryPct={coShare}
                                            primaryClass="barCompany"
                                            secondaryClass="barLeaseOwn"
                                            title={`Company ${(coShare * 100).toFixed(0)}% / Lease-Own ${((1 - coShare) * 100).toFixed(0)}%`}
                                        />
                                        <div className={styles.totalGrossLegend}>
                                            <span><i className={`${styles.dot} ${styles.barCompany}`} /> {(coShare * 100).toFixed(0)}%</span>
                                            <span>{((1 - coShare) * 100).toFixed(0)}% <i className={`${styles.dot} ${styles.barLeaseOwn}`} /></span>
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {/* ---------------- EXPENSES ---------------- */}
                        <SectionRow label="Expenses" subLabel="Share-of-expenses bar per row" />
                        {(() => {
                            // Per-period totals used to normalize the inline share bars.
                            const perPeriodTotal = visiblePeriods.map(p => {
                                const d = getPeriodData(p);
                                return d && !isFleetPeriodNoData(d) ? computeDerived(d).totalExp : 0;
                            });
                            const renderExpenseCell = (amount, idx) => {
                                const total = perPeriodTotal[idx] || 0;
                                const share = total > 0 ? amount / total : 0;
                                return (
                                    <div className={styles.expCell}>
                                        <div className={styles.expValue}>{fmtMoney(amount)}</div>
                                        <div className={styles.expMiniBar}>
                                            <div className={styles.expMiniBarFill} style={{ width: `${share * 100}%` }} />
                                        </div>
                                    </div>
                                );
                            };
                            return (
                                <>
                                    <CustomRow label="Salary — Lease/Own" kind="muted" renderCell={(d, _p, idx) => renderExpenseCell(d.salaryLeaseOwn, idx)} />
                                    <CustomRow label="Salary — Company"   kind="muted" renderCell={(d, _p, idx) => renderExpenseCell(d.salaryCompany,  idx)} />
                                    {dynamicExpenseRows.map(row => {
                                        if (row.type === 'otherEfs') {
                                            return (
                                                <React.Fragment key={OTHER_EFS_LABEL}>
                                                    <CustomRow
                                                        label={OTHER_EFS_LABEL}
                                                        kind="muted"
                                                        expandable
                                                        expanded={otherEfsExpanded}
                                                        onLabelClick={() => setOtherEfsExpanded(v => !v)}
                                                        renderCell={(d, _p, idx) => {
                                                            // When expanded, hide the group total so column
                                                            // line items sum to Total Expenses without double-count.
                                                            // Keep the same cell structure so row height is unchanged.
                                                            if (otherEfsExpanded) {
                                                                return (
                                                                    <div className={styles.expCell} aria-hidden>
                                                                        <div className={styles.expValue} style={{ visibility: 'hidden' }}>
                                                                            {fmtMoney(0)}
                                                                        </div>
                                                                        <div className={styles.expMiniBar} style={{ visibility: 'hidden' }}>
                                                                            <div className={styles.expMiniBarFill} style={{ width: 0 }} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                            const amount = row.detailLabels.reduce(
                                                                (s, label) => s + findExpenseAmount(d, label),
                                                                0,
                                                            );
                                                            return renderExpenseCell(amount, idx);
                                                        }}
                                                    />
                                                    {otherEfsExpanded && row.detailLabels.map(label => (
                                                        <CustomRow
                                                            key={label}
                                                            label={label}
                                                            kind="muted"
                                                            indented
                                                            renderCell={(d, _p, idx) => renderExpenseCell(findExpenseAmount(d, label), idx)}
                                                        />
                                                    ))}
                                                </React.Fragment>
                                            );
                                        }
                                        return (
                                            <CustomRow
                                                key={row.label}
                                                label={row.label}
                                                kind="muted"
                                                renderCell={(d, _p, idx) => renderExpenseCell(findExpenseAmount(d, row.label), idx)}
                                            />
                                        );
                                    })}
                                </>
                            );
                        })()}
                        <MetricRow
                            label="Total Expenses"
                            kind="total"
                            accessor={(d) => fmtMoney(computeDerived(d).totalExp)}
                            valueClassAccessor={() => 'valueExpense'}
                        />

                        <CustomRow
                            label="Operating Profit"
                            kind="total"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                const positive = der.opProfit >= 0;
                                const expShare = der.totalGross > 0 ? Math.min(1, Math.max(0, der.totalExp / der.totalGross)) : 1;
                                return (
                                    <div className={styles.profitCell}>
                                        <div className={`${styles.profitValue} ${positive ? styles.positive : styles.negative}`}>
                                            {fmtMoney(der.opProfit)}
                                        </div>
                                        <div
                                            className={styles.flowCell}
                                            title={`Expenses ${(expShare * 100).toFixed(0)}% · Profit ${((1 - expShare) * 100).toFixed(0)}%`}
                                        >
                                            <div className={styles.flowBar}>
                                                <div className={styles.flowBarExpenses} style={{ flexBasis: `${expShare * 100}%` }} />
                                                <div className={`${styles.flowBarProfit} ${positive ? '' : styles.flowBarProfitNeg}`} style={{ flexBasis: `${(1 - expShare) * 100}%` }} />
                                            </div>
                                            <div className={styles.flowLegend}>
                                                <span>{(expShare * 100).toFixed(0)}% exp</span>
                                                <span>{((1 - expShare) * 100).toFixed(0)}% profit</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {/* ------ PROFIT AFTER DRIVER PAY & FUEL ------ */}
                        <SectionRow
                            label="Profit after Driver Pay & Fuel"
                            subLabel="Gross − Salary − Fuel · Company | Lease/Own"
                        />
                        <CustomRow
                            label="Gross"
                            kind="muted"
                            renderCell={(d) => (
                                <PayFuelSegmentCell
                                    companyAmt={d.grossCompany}
                                    leaseAmt={d.grossLeaseOwn}
                                    showHeads
                                />
                            )}
                        />
                        <CustomRow
                            label="Salary"
                            kind="muted"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                return (
                                    <PayFuelSegmentCell
                                        companyAmt={d.salaryCompany}
                                        leaseAmt={d.salaryLeaseOwn}
                                        companyPct={der.salaryPctCompany}
                                        leasePct={der.salaryPctLeaseOwn}
                                    />
                                );
                            }}
                        />
                        <CustomRow
                            label="Fuel"
                            kind="muted"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                return (
                                    <PayFuelSegmentCell
                                        companyAmt={der.fuelCompany}
                                        leaseAmt={der.fuelLeaseOwn}
                                        companyPct={der.fuelPctCompany}
                                        leasePct={der.fuelPctLeaseOwn}
                                    />
                                );
                            }}
                        />
                        <CustomRow
                            label="Profit"
                            kind="total"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                return (
                                    <PayFuelSegmentCell
                                        companyAmt={der.profitCompany}
                                        leaseAmt={der.profitLeaseOwn}
                                        companyPct={der.profitPctCompany}
                                        leasePct={der.profitPctLeaseOwn}
                                        emphasize
                                    />
                                );
                            }}
                        />

                        {/* ---------------- FLEET ---------------- */}
                        <SectionRow label="Fleet" subLabel="Utilized trucks and available capacity" />
                        <MetricRow label="Trucks Utilized — Company"    kind="muted" accessor={(d) => fmtCount1(d.companyTruckCount)} />
                        <MetricRow label="Trucks Utilized — Lease/Own"  kind="muted" accessor={(d) => fmtCount1(d.leaseOwnTruckCount)} />
                        <CustomRow
                            label="Utilization"
                            kind="total"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                return (
                                    <div className={styles.utilWrap}>
                                        <UtilizationCell value={der.utilization} />
                                        <div className={styles.utilCaption}>
                                            {fmtCount1(der.totalTrucksUtilized)} <small>/ {fmtCount1(d.totalAvailableTrucks)}</small>
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        <SectionRow
                            label="Driver payout"
                            subLabel="Payout as a share of each segment's gross"
                        />
                        <MetricRow
                            label="Company Drivers' Payout %"
                            kind="muted"
                            accessor={(d) => fmtPct(computeDerived(d).driverSalaryPctCompany)}
                        />
                        <MetricRow
                            label="Lease/Own Drivers' Payout %"
                            kind="muted"
                            accessor={(d) => fmtPct(computeDerived(d).driverSalaryPctLeaseOwn)}
                        />

                        {/* ---------------- MILEAGE ---------------- */}
                        <SectionRow label="Mileage" />
                        <MetricRow label="Miles — Company"   kind="muted" accessor={(d) => fmtMiles(d.milesCompany)} />
                        <MetricRow label="Miles — Lease/Own" kind="muted" accessor={(d) => fmtMiles(d.milesLeaseOwn)} />
                        <CustomRow
                            label="Total Miles"
                            kind="total"
                            renderCell={(d) => {
                                const der = computeDerived(d);
                                const coShare = der.totalMiles > 0 ? d.milesCompany / der.totalMiles : 0;
                                return (
                                    <div className={styles.totalGrossCell}>
                                        <div className={styles.totalGrossValue}>{fmtMiles(der.totalMiles)}</div>
                                        <ProportionBar
                                            primaryPct={coShare}
                                            primaryClass="barCompany"
                                            secondaryClass="barLeaseOwn"
                                            title={`Company ${(coShare * 100).toFixed(0)}% / Lease-Own ${((1 - coShare) * 100).toFixed(0)}%`}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <MetricRow label="Avg Miles / Truck — Company"   accessor={(d) => fmtMiles(computeDerived(d).avgMileCompany)} />
                        <MetricRow label="Avg Miles / Truck — Lease/Own" accessor={(d) => fmtMiles(computeDerived(d).avgMileLeaseOwn)} />

                        {/* ---------------- UNIT ECONOMICS ---------------- */}
                        <SectionRow label="Unit Economics" subLabel="Per-truck and per-mile ratios" />
                        <MetricRow label="Avg Gross / Truck — Company"   kind="muted" accessor={(d) => fmtMoney(computeDerived(d).avgGrossCompany)} />
                        <MetricRow label="Avg Gross / Truck — Lease/Own" kind="muted" accessor={(d) => fmtMoney(computeDerived(d).avgGrossLeaseOwn)} />
                        <MetricRow label="Total Avg Gross / Truck"       kind="total" accessor={(d) => fmtMoney(computeDerived(d).totalAvgGross)} />

                        <MetricRow label="RPM — Company"   kind="muted" accessor={(d) => fmtRate(computeDerived(d).rpmCompany)} />
                        <MetricRow label="RPM — Lease/Own" kind="muted" accessor={(d) => fmtRate(computeDerived(d).rpmLeaseOwn)} />
                        <MetricRow label="Total RPM"       kind="total" accessor={(d) => fmtRate(computeDerived(d).totalRpm)} />

                        <MetricRow label="Driver Cost / Mi — Company"  accessor={(d) => fmtRate(computeDerived(d).driverCostPerMiCompany)} />
                        <MetricRow label={`${FUEL_LABEL} Cost / Mi`}   accessor={(d) => fmtRate(computeDerived(d).fuelCostPerMi)} />
                        <MetricRow label="Maintenance Cost / Mi"       accessor={(d) => fmtRate(computeDerived(d).maintenanceCostPerMi)} />
                    </div>
                </div>
            )}
        </div>
    );
};
